import React, { useState, useEffect } from "react";
import {
    Box,
    Grid,
    GridItem,
    Select,
    Slider,
    SliderTrack,
    SliderFilledTrack,
    SliderThumb,
    Text,
    Heading,
    VStack,
    Button,
    HStack,
    Image,
} from "@chakra-ui/react";
import { ArrowBackIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import ScatterPlot from "./ScatterPlot";
import { ENDPOINT } from "views/Dashboard/Dashboard/api";

const UnisonoGraphs = () => {
    const [winWidth, setWinWidth] = useState(window.innerWidth);
    const [dailyEntriesPP, setDailyEntriesPP] = useState([]);
    const [daysAvgK, setDaysAvgK] = useState(3);
    const [daysMax, setDaysMax] = useState(150);
    const [featureNames, setFeatureNames] = useState([]);
    const [selectedFeature, setSelectedFeature] = useState("OQ1_mean");
    const [patientIds, setPatientIds] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState("");
    const [featureInfo, setFeatureInfo] = useState(null);
    const [totalPatients, setTotalPatients] = useState(0);

    const [currentPage, setCurrentPage] = useState(1);
    const patientsPerPage = 15;

    // const ENDPOINT = "https://unisono-bck.zana.ai/zf";
    // const ENDPOINT = "http://127.0.0.1:8000";

    const fetchData = async (url) => {
        try {
            const response = await fetch(`${ENDPOINT}${url}`);
            if (!response.ok) {
                throw new Error(`Error fetching data: ${response.statusText}`);
            }
            const jsonData = await response.json();
            return jsonData.data;
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    const fetchFeatureInfo = async (featureName) => {
        try {
            const response = await fetch(
                `${ENDPOINT}/feature-info?feature=${featureName}`
            );
            if (!response.ok) {
                throw new Error(`Error fetching feature info: ${response.statusText}`);
            }
            const jsonData = await response.json();
            setFeatureInfo(jsonData.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            const patients = await fetchData("/patient-ids");
            setPatientIds(patients || []);
            setTotalPatients(patients ? patients.length : 0);

            const features = await fetchData("/feature-names");
            setFeatureNames(features || []);
        };

        fetchInitialData();

        const handleResize = () => setWinWidth(window.innerWidth);
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        const fetchDataForPatientAndFeature = async () => {
            let url = `/features?feature=${selectedFeature}`;
            if (selectedPatient) {
                url += `&patientId=${selectedPatient}`;
            }

            const data = await fetchData(url);
            setDailyEntriesPP(Object.entries(data || {}));

            console.log("setDailyEntriesPP", data);

        };

        fetchDataForPatientAndFeature();
        fetchFeatureInfo(selectedFeature);
    }, [selectedPatient, selectedFeature]);

    const handlePatientChange = async (e) => {
        const selected = e.target.value;
        setSelectedPatient(selected);
        setCurrentPage(1); // Reset to page 1 when patient selection changes
    };

    const handleFeatureChange = async (e) => {
        const selected = e.target.value;
        setSelectedFeature(selected);
    };

    // Pagination logic for when "All Patients" is selected
    const indexOfLastPatient = currentPage * patientsPerPage;
    const indexOfFirstPatient = indexOfLastPatient - patientsPerPage;
    const currentPatients = patientIds.slice(
        indexOfFirstPatient,
        indexOfLastPatient
    );

    const nextPage = () => {
        if (currentPage < Math.ceil(patientIds.length / patientsPerPage)) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <Grid
            templateColumns={{ base: "1fr", md: "2fr 1fr" }}
            gap={3}
            p={4}
            mt={20}
        >
            {/* Controls (Total Patients, Patient ID, Features, Legend, and Pagination) */}
            <GridItem order={{ base: 1, md: 2 }}>
                <Box mb={8}>
                    <Heading as="h5" size="m" mb={6}>
                        Select Patient ID
                    </Heading>
                    <Select value={selectedPatient} onChange={handlePatientChange}>
                        <option value="">All Patients</option>
                        {patientIds.map((id) => (
                            <option key={id} value={id}>
                                {id}
                            </option>
                        ))}
                    </Select>
                </Box>

                {/* Feature selection, Mean Days, Max Days Sliders */}
                <Box p={4} boxShadow="md" borderRadius="md" mb={8}>
                    <VStack align="stretch" spacing={4}>
                        <Box>
                            <Text mb={2}>Feature</Text>
                            <Select value={selectedFeature} onChange={handleFeatureChange}>
                                {featureNames.map((fname) => (
                                    <option key={fname} value={fname}>
                                        {fname}
                                    </option>
                                ))}
                            </Select>
                        </Box>

                        {/* Mean Days Slider */}
                        <Box>
                            <Text mb={2}>Mean Days</Text>
                            <Slider min={1} max={9} value={daysAvgK} onChange={setDaysAvgK}>
                                <SliderTrack>
                                    <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb />
                            </Slider>
                            <Text mt={2} textAlign="center">
                                {daysAvgK}
                            </Text>
                        </Box>

                        {/* Max Days Slider */}
                        <Box>
                            <Text mb={2}>Max Days</Text>
                            <Slider min={14} max={150} value={daysMax} onChange={setDaysMax}>
                                <SliderTrack>
                                    <SliderFilledTrack />
                                </SliderTrack>
                                <SliderThumb />
                            </Slider>
                            <Text mt={2} textAlign="center">
                                {daysMax}
                            </Text>
                        </Box>
                    </VStack>
                </Box>

                {/* Feature Information Section */}
                {featureInfo && (
                    <Box mb={8} p={4} boxShadow="md" borderRadius="md">
                        <Heading as="h4" size="md" mb={4}>
                            Feature Information
                        </Heading>
                        {featureInfo.featureNameFull && (
                            <Text fontWeight="bold" mb={2}>
                                {featureInfo.featureNameFull}
                            </Text>
                        )}
                        {featureInfo.featureDetail && (
                            <Text>{featureInfo.featureDetail}</Text>
                        )}
                    </Box>
                )}

                {/* Legend for the Scatter Plot */}
                <Box mb={6} p={4}>
                    <Heading as="h4" size="md" mb={4}>
                        Legend
                    </Heading>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" alignItems="center">
                            <Box
                                w="16px"
                                h="16px"
                                bg="#2b6cb0"
                                mr={2}
                                borderRadius="50%"
                                flexShrink={0}
                            ></Box>
                            <Text>
                                Original data points (blue). Select a day by clicking on its
                                data point.
                            </Text>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Box
                                w="16px"
                                h="16px"
                                bg="#E53E3E"
                                mr={2}
                                borderRadius="50%"
                                flexShrink={0}
                            ></Box>
                            <Text>Mean values (red)</Text>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Box
                                w="16px"
                                h="16px" 
                                bg="#ED8936"
                                mr={2}   
                                flexShrink={0}                            
                            ></Box>
                            <Text>Events (admission, discharge, 6-week day/s) (orange)</Text>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Box
                                w="16px"
                                h="16px"                              
                                bg="#FBB6CE"
                                mr={2}   
                                flexShrink={0}                      
                            ></Box>
                            <Text>Day on which the patient weight was recorded (pink)</Text>
                        </Box>
                    </Box>
                </Box>
            </GridItem>

            {/* Scatter Plot */}
            <GridItem order={{ base: 2, md: 1 }}>
                <Box w="100%" p={4}>
                    <Heading as="h2" size="lg" mb={8} textAlign={"center"}>
                        Total Patients: {totalPatients}
                    </Heading>

                    {dailyEntriesPP.length > 0 ? (
                        dailyEntriesPP
                            // If "All Patients" is selected, apply pagination, else show the selected patient
                            .filter(([patientId]) =>
                                selectedPatient
                                    ? patientId === selectedPatient
                                    : currentPatients.includes(patientId)
                            )
                            .map(([patientId, value]) => (
                                <ScatterPlot
                                    key={patientId}
                                    title={`Patient ID: ${patientId}`}
                                    feature={selectedFeature}
                                    data={value.feature}
                                    events={[value.events]}
                                    weights={value.weight}
                                    avgk={daysAvgK}
                                    daysMax={daysMax}
                                    width={Math.min(1000, 0.6 * winWidth)}
                                    height={200}
                                />
                            ))
                    ) : (
                        <Text>Loading data.</Text>
                    )}
                </Box>

                {/* HF Risk Chart: Only show when a specific patient is selected */}
                {selectedPatient && (
                    <Box w="100%" p={4} mt={8}>
                        <Heading as="h3" size="md" mb={4} textAlign="center">
                            HF Risk Stratification Chart
                        </Heading>
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Image
                                src={`${process.env.PUBLIC_URL}/assets/hf-risk-chart.png`}
                                alt="HF Risk Stratification Chart"
                                maxW="100%"
                                height="auto"
                                objectFit="contain"
                            />
                        </Box>
                    </Box>
                )}

                {/* Pagination Controls: Only show when "All Patients" is selected */}
                {!selectedPatient && (
                    <Box mb={10}>
                        <HStack justifyContent="space-between">
                            <Button
                                onClick={prevPage}
                                isDisabled={currentPage === 1}
                                leftIcon={<ArrowBackIcon />}
                            >
                                Previous
                            </Button>
                            <Text>
                                Page {currentPage} of{" "}
                                {Math.ceil(patientIds.length / patientsPerPage)}
                            </Text>
                            <Button
                                onClick={nextPage}
                                isDisabled={
                                    currentPage === Math.ceil(patientIds.length / patientsPerPage)
                                }
                                rightIcon={<ArrowForwardIcon />}
                            >
                                Next
                            </Button>
                        </HStack>
                    </Box>
                )}
            </GridItem>
        </Grid>
    );
};

export default UnisonoGraphs;
